import React, { useEffect } from "react";
import { Typography, Box, Paper, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";
import { useNavigate } from "react-router-dom";
import CommonBackground from "../components/CommonBackground";
import { useAdminRole } from "../hooks/useAdminRole";
import type { User } from "firebase/auth";

const MotionPaper = motion(Paper);

interface ProtectedRouteProps {
  user: User | null;
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ user, children }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Redirect unverified users to auth page
  useEffect(() => {
    if (user && !user.emailVerified) {
      navigate("/auth");
    }
  }, [user, navigate]);

  if (!user) {
    const isDark = theme.palette.mode === "dark";

    return (
      <CommonBackground>
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MotionPaper
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            elevation={6}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              textAlign: "center",
              backdropFilter: "blur(12px) saturate(180%)",
              background: "transparent",
              border: isDark
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.1)",
              boxShadow: isDark
                ? "0 10px 30px rgba(0,0,0,0.4)"
                : "0 10px 30px rgba(0,0,0,0.1)",
              color: isDark ? "#fff" : "#111",
              maxWidth: 500,
            }}
          >
            <LockOutlinedIcon
              color="error"
              sx={{
                fontSize: 60,
                mb: 2,
                filter: "drop-shadow(0 0 8px red)",
              }}
            />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Restricted Access
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
              }}
            >
              🚫 You are not authorized to view this page. <br />
              Please sign in to continue or return to the homepage.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/auth")}
              sx={{
                borderRadius: 10,
                textTransform: "none",
                px: 4,
                py: 1.2,
                background: "linear-gradient(45deg, #ff1744, #f50057)",
                boxShadow: "0 4px 20px rgba(255, 23, 68, 0.4)",
              }}
            >
              Go to Login
            </Button>
          </MotionPaper>
        </Box>
      </CommonBackground>
    );
  }

  // Check if user email is verified
  if (!user.emailVerified) {
    const isDark = theme.palette.mode === "dark";

    return (
      <CommonBackground>
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MotionPaper
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            elevation={6}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              textAlign: "center",
              backdropFilter: "blur(12px) saturate(180%)",
              background: "transparent",
              border: isDark
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.1)",
              boxShadow: isDark
                ? "0 10px 30px rgba(0,0,0,0.4)"
                : "0 10px 30px rgba(0,0,0,0.1)",
              color: isDark ? "#fff" : "#111",
              maxWidth: 500,
            }}
          >
            <EmailIcon
              color="warning"
              sx={{
                fontSize: 60,
                mb: 2,
                filter: "drop-shadow(0 0 8px orange)",
              }}
            />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Email Verification Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
              }}
            >
              📧 Please verify your email address to access this page. <br />
              Check your inbox for the verification link.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/auth")}
              sx={{
                borderRadius: 10,
                textTransform: "none",
                px: 4,
                py: 1.2,
                background: "linear-gradient(45deg, #ff1744, #f50057)",
                boxShadow: "0 4px 20px rgba(255, 23, 68, 0.4)",
              }}
            >
              Go to Verification
            </Button>
          </MotionPaper>
        </Box>
      </CommonBackground>
    );
  }

  return <>{children}</>;
};

interface AdminProtectedRouteProps {
  user: User | null;
  children: React.ReactNode;
}

export const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
  user,
  children,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isModerator: userIsModerator, loading } = useAdminRole(user);

  if (loading) {
    return (
      <CommonBackground>
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </CommonBackground>
    );
  }

  if (!user) {
    return <ProtectedRoute user={null}>{null}</ProtectedRoute>;
  }

  // Check if user email is verified
  if (!user.emailVerified) {
    return <ProtectedRoute user={user}>{null}</ProtectedRoute>;
  }

  if (!userIsModerator) {
    const isDark = theme.palette.mode === "dark";

    return (
      <CommonBackground>
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MotionPaper
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            elevation={6}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              textAlign: "center",
              backdropFilter: "blur(12px) saturate(180%)",
              background: "transparent",
              border: isDark
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.1)",
              boxShadow: isDark
                ? "0 10px 30px rgba(0,0,0,0.4)"
                : "0 10px 30px rgba(0,0,0,0.1)",
              color: isDark ? "#fff" : "#111",
              maxWidth: 500,
            }}
          >
            <AdminPanelSettingsIcon
              color="error"
              sx={{
                fontSize: 60,
                mb: 2,
                filter: "drop-shadow(0 0 8px red)",
              }}
            />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Admin Access Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
              }}
            >
              🔒 This page is restricted to administrators and moderators only.
              <br />
              You do not have permission to access this area.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/")}
              sx={{
                borderRadius: 10,
                textTransform: "none",
                px: 4,
                py: 1.2,
                background: "linear-gradient(45deg, #ff1744, #f50057)",
                boxShadow: "0 4px 20px rgba(255, 23, 68, 0.4)",
              }}
            >
              Return to Home
            </Button>
          </MotionPaper>
        </Box>
      </CommonBackground>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
